import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { Reveal, Eyebrow, LineReveal } from "../components/motion";
import { IMAGES } from "../config/site";

function Hero() {
  return (
    <section className="relative h-[72svh] min-h-[520px] overflow-hidden flex items-end" data-testid="about-hero">
      <div className="absolute inset-0">
        <img src={IMAGES.overview} alt="The lavender farm valley at Danseys Pass" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/45 to-ink/40" />
      </div>
      <div className="relative z-10 max-w-6xl mx-auto w-full px-6 md:px-8 pb-16">
        <Eyebrow className="mb-6">Our Story</Eyebrow>
        <LineReveal lines={["Two brothers,", "one quiet valley."]} className="display-1 text-cream" />
      </div>
    </section>
  );
}

function Meet() {
  return (
    <section className="py-24 md:py-32 px-6 md:px-16" data-testid="about-meet-section">
      <div className="max-w-6xl mx-auto grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7">
          <Reveal><Eyebrow className="mb-6">From Australia to Aotearoa</Eyebrow></Reveal>
          <Reveal delay={0.08}>
            <h2 className="display-2 text-cream">
              We fell for the <span className="serif-italic text-lavglow">landscapes first.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.14}>
            <p className="text-sage mt-8 leading-relaxed text-sm md:text-base">
              We're Tim and Hugh — brothers, born and raised in Australia. Around fifteen years
              ago we both made the move across the Tasman to Aotearoa New Zealand, and it didn't
              take long to fall in love. With the landscapes first — the fiords, the ranges, the
              rivers running cold and clear. Then the slower pace of life, and the way people
              here take real time for each other.
            </p>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="text-fern mt-5 leading-relaxed text-sm md:text-base">
              We built our lives here. And somewhere along the way, we started looking for
              something of our own to create in the country that had given us so much.
            </p>
          </Reveal>
        </div>
        <div className="lg:col-span-5">
          <Reveal y={40}>
            <div className="img-frame aspect-[2/3] max-w-[380px] mx-auto lg:ml-auto">
              <img src="/images/about-family.jpg" alt="Tim with family, early days in New Zealand" className="h-full w-full object-cover" data-testid="about-family-photo" />
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="font-mono text-[0.6rem] uppercase tracking-widest2 text-fern mt-4 text-center lg:text-left">
              Family first, wherever we land
            </p>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function HighCountry() {
  return (
    <section className="pb-24 md:pb-32 px-6 md:px-16" data-testid="about-highcountry-section">
      <div className="max-w-6xl mx-auto grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7 order-2 lg:order-1">
          <Reveal y={40}>
            <div className="img-frame aspect-[4/3]">
              <img src="/images/about-horse.jpg" alt="Riding into the high country above the valley" className="h-full w-full object-cover" data-testid="about-horse-photo" />
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="font-mono text-[0.6rem] uppercase tracking-widest2 text-fern mt-4">
              The best way to see this country — the slow way, on horseback
            </p>
          </Reveal>
        </div>
        <div className="lg:col-span-5 order-1 lg:order-2">
          <Reveal><Eyebrow className="mb-6">Why Danseys Pass</Eyebrow></Reveal>
          <Reveal delay={0.08}>
            <h2 className="display-2 text-cream">It felt like <span className="serif-italic text-lavglow">something different.</span></h2>
          </Reveal>
          <Reveal delay={0.14}>
            <p className="text-sage mt-8 leading-relaxed text-sm md:text-base">
              We'd both travelled the famous routes — Queenstown, Wānaka, the well-worn postcards.
              Beautiful, yes, and busy. Then we found Danseys Pass: a remote high-country valley
              in North Otago, where the road narrows, the noise falls away and the crowds never
              arrive. Beautiful in a way that felt ours alone.
            </p>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function History() {
  return (
    <section className="py-24 md:py-32 px-6 md:px-16 bg-moss" data-testid="about-history-section">
      <div className="max-w-6xl mx-auto grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <Reveal><Eyebrow className="mb-6">The place & its past</Eyebrow></Reveal>
          <Reveal delay={0.08}>
            <h2 className="display-2 text-cream">A road with <span className="serif-italic text-lavglow">history.</span></h2>
          </Reveal>
          <Reveal delay={0.14}>
            <p className="text-sage mt-8 leading-relaxed text-sm md:text-base">
              This country has been travelled for generations. The coach road over Danseys Pass
              was carved through schist country in the gold-rush days of the 1860s, linking the
              Otago diggings with the coast, and the historic Danseys Pass Hotel has welcomed
              travellers over the pass since 1862.
            </p>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="text-fern mt-5 leading-relaxed text-sm md:text-base">
              Shepherds, miners and stockmen moved stock and gold along this route; today it's
              one of Otago's quietest, most beautiful drives — ending, in our case, at a
              riverside valley where nothing much has changed.
            </p>
          </Reveal>
        </div>
        <div className="lg:col-span-7">
          <Reveal delay={0.1} y={40}>
            <div className="img-frame aspect-[16/10]">
              <img src={IMAGES.frost} alt="Frost over the high-country paddocks" className="h-full w-full object-cover" />
            </div>
          </Reveal>
          <div className="grid grid-cols-2 gap-4 mt-4">
            <Reveal y={30} delay={0.16}>
              <div className="img-frame aspect-square">
                <video
                  src="/videos/farm.mp4"
                  autoPlay
                  muted
                  loop
                  playsInline
                  preload="auto"
                  poster="/images/farm-poster.jpg"
                  className="h-full w-full object-cover"
                  data-testid="about-grazing-video"
                />
              </div>
            </Reveal>
            <Reveal y={30} delay={0.22}>
              <div className="img-frame aspect-square md:mt-8">
                <img src={IMAGES.oteake} alt="Oteake Conservation Park sign above the farm" className="h-full w-full object-cover" />
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}

function Different() {
  return (
    <section className="py-24 md:py-32 px-6 md:px-16" data-testid="about-different-section">
      <div className="max-w-4xl mx-auto text-center">
        <Reveal><Eyebrow className="mb-6">A different New Zealand</Eyebrow></Reveal>
        <Reveal delay={0.08}>
          <h2 className="display-2 text-cream">
            Somewhere the <span className="serif-italic text-lavglow">busy world</span> doesn't reach.
          </h2>
        </Reveal>
        <Reveal delay={0.16}>
          <p className="text-sage mt-8 leading-relaxed text-sm md:text-base max-w-2xl mx-auto">
            We wanted to create something out here that gives visitors a different experience of
            New Zealand — not the tourist meccas, but the quiet high country: a working lavender
            farm by the Maerewhenua River, two geodesic domes under big southern skies, private
            hot tubs, a riverside sauna, and the kind of quiet you can hear.
          </p>
        </Reveal>
        <Reveal delay={0.22}>
          <p className="serif-italic text-2xl md:text-3xl text-sage mt-10 leading-snug">
            “This is a place we think is worth sharing.”
          </p>
        </Reveal>
        <Reveal delay={0.3} className="mt-10 flex flex-wrap justify-center gap-4">
          <Link to="/domes" className="btn-primary" data-testid="about-explore-domes-button">
            Explore the domes
          </Link>
          <Link to="/book" className="btn-ghost" data-testid="about-book-button">
            Book your stay
            <ArrowUpRight size={15} />
          </Link>
        </Reveal>
      </div>
    </section>
  );
}

export default function About() {
  return (
    <>
      <Hero />
      <Meet />
      <HighCountry />
      <History />
      <Different />
    </>
  );
}